import React from 'react';
import { extractApiErrorMessage, notifications } from '../../utils';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  CircularProgress,
  Checkbox,
  Typography,
  FormControlLabel,
  Switch,
  TableContainer,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Tooltip
} from '@material-ui/core';
import {
  MedicalSupplyOrderViewModel,
  MedicalSupplyOrderItemViewModel,
  MedicalSupplyOrderStatus
} from '../../models/injection';
import { useStyles } from './styles';
import { Title } from '../../elements';
import { MedicalSuppliesService } from '../../hooks';
import { renderSupplyRowLocations } from '../../components/OrderMedicalSupplyModal/component';
import { useTranslation } from 'react-i18next';
import { SupplyAmountCell } from '../../components/MedicalSupplyOrders/component';
import CloseIcon from '@material-ui/icons/Close';

interface ConfirmSupplyOrderModalProps {
  opened: boolean;
  order: MedicalSupplyOrderViewModel;
  onModalAction: (result: boolean) => void;
}

const ConfirmSupplyOrderModal: React.FC<any> = ({ opened, order, onModalAction }: ConfirmSupplyOrderModalProps) => {
  const medicalSuppliesService = new MedicalSuppliesService();
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [isBusy, setIsBusy] = React.useState(false);
  const [orderItems, setOrderItems] = React.useState([] as MedicalSupplyOrderItemViewModel[]);
  const [successTreatment, setSuccessTreatment] = React.useState(false);
  const [animalsVaccinability, setAnimalsVaccinability] = React.useState(false);
  const [successVaccination, setSuccessVaccination] = React.useState(false);

  React.useEffect(() => {
    if (order && order.items) {
      const items = [...order.items];
      items.forEach((o) => {
        o.realAmount = o.amount;
      });
      setOrderItems(items);
    }
  }, [opened]);

  const closeDialog = (result: boolean) => {
    if (onModalAction) {
      onModalAction(result);
    }

    clearFields();
  };

  const handleConfirmOrder = () => {
    if (!validForm()) {
      return;
    }

    setIsBusy(true);

    const confirmOrderCommand = {
      orderId: order.id,
      items: orderItems,
      successTreatment,
      animalsVaccinability,
      successVaccination
    };

    medicalSuppliesService
      .confirmMedicalSupplyOrder(confirmOrderCommand)
      .then(() => {
        notifications.success(t('f-supp-order-completed-ok'));
        closeDialog(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  const validForm = (): boolean => {
    // ..

    return true;
  };

  const clearFields = () => {
    setOrderItems([]);
    setSuccessTreatment(false);
    setSuccessVaccination(false);
    setAnimalsVaccinability(false);
    setIsBusy(false);
  };

  const renderOrderItemsSectionHeader = () => {
    return (
      <Grid item container direction="row" md={12}>
        <Grid item container md={8} spacing={1}>
          <Grid item>
            <Icon color="primary" fontSize="large">
              {'shopping_bag'}
            </Icon>
          </Grid>
          <Grid item>
            <Title>{t('f-supp-order-items')}</Title>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderDetailsSectionHeader = () => {
    return (
      <Grid item container direction="row" md={12}>
        <Grid item container md={8} spacing={1}>
          <Grid item>
            <Icon color="primary" fontSize="large">
              {'comment'}
            </Icon>
          </Grid>
          <Grid item>
            <Title>{t('f-supp-order-details')}</Title>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderDetailsSectionContent = () => {
    return (
      <Grid item container direction="column" spacing={2}>
        {/* details fields */}
        <Grid item container direction="row" spacing={2}>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={successTreatment}
                  onChange={(e) => setSuccessTreatment(e.target.checked)}
                  color="primary"
                />
              }
              label={t('f-supp-order-succ-treat')}
            />
          </Grid>

          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={animalsVaccinability}
                  onChange={(e) => setAnimalsVaccinability(e.target.checked)}
                  color="primary"
                />
              }
              label={t('f-supp-order-anim-vac')}
            />
          </Grid>

          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={successVaccination}
                  onChange={(e) => setSuccessVaccination(e.target.checked)}
                  color="primary"
                />
              }
              label={t('f-supp-order-succ-vac')}
            />
          </Grid>
        </Grid>

        {/* overview fields */}
        <Grid item container direction="row" spacing={2}>
          <Grid item xs={3}>
            <Typography variant="overline">{t('f-supp-order-number')}</Typography>
            <Typography variant="body1">{order.number || '-'}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="overline">{t('f-supp-order-comments')}</Typography>
            <Typography variant="body1">{order.details?.comments || '-'}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="overline">{t('f-com-created')}</Typography>
            <Typography variant="body1">{new Date(order.created).toLocaleString() || '-'}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="overline">{t('f-supp-order-status')}</Typography>
            <Typography variant="body1">{MedicalSupplyOrderStatus[order.status] || '-'}</Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderOrderItemsTable = () => {
    return (
      <Grid item xs={12}>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>{t('f-com-supply')}</TableCell>
                <TableCell align="center">{t('f-supp-order-locations')}</TableCell>
                <TableCell>{t('f-supp-order-symp')}</TableCell>
                <TableCell>{t('f-supp-order-diag')}</TableCell>
                <TableCell>{t('f-supp-order-amount-del')}</TableCell>
                <TableCell>{t('f-supp-order-recur-treat')}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {order &&
                order.items.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell align="center">{SupplyAmountCell(item, order)}</TableCell>
                    <TableCell>{`${item.medicalSupplyBottle.medicalSupply.number} ${item.medicalSupplyBottle.size}`}</TableCell>
                    <TableCell align="center">{renderSupplyRowLocations(item, index)}</TableCell>
                    <TableCell>{item.details.symptoms}</TableCell>
                    <TableCell>{renderOrderItemDiagnosisCell(item, index)}</TableCell>
                    <TableCell>{renderOrderItemDeliveredAmountCell(item, index)}</TableCell>
                    <TableCell>{renderOrderItemRecurrentTreatCell(item, index)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    );
  };

  const renderOrderItemDiagnosisCell = (item: MedicalSupplyOrderItemViewModel, index: number) => {
    return (
      <TextField
        id={`diag-${index}`}
        type="text"
        size="small"
        multiline
        variant="outlined"
        label={t('f-supp-order-diag')}
        className={classes.diagInput}
        value={item.details.diagnosis || ''}
        onChange={(e) => {
          handleOrderItemDiagnosisChange(e.target.value, index);
        }}
      />
    );
  };

  const renderOrderItemDeliveredAmountCell = (item: MedicalSupplyOrderItemViewModel, index: number) => {
    return (
      <TextField
        id={`delivered-${index}`}
        type="number"
        size="small"
        variant="outlined"
        label={undefined}
        className={classes.amountInput}
        value={item.realAmount}
        onChange={(e) => {
          const newValue = Number(e.target.value);
          if (newValue >= 0) {
            handleOrderItemDeliveredAmountChange(newValue, index);
          }
        }}
      />
    );
  };

  const renderOrderItemRecurrentTreatCell = (item: MedicalSupplyOrderItemViewModel, index: number) => {
    return (
      <Switch
        checked={item.details.recurrentTreatment}
        onChange={(e) => handleRecurrentTreatChange(e, index)}
        color="primary"
      />
    );
  };

  const handleRecurrentTreatChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    orderItems[index].details.recurrentTreatment = event.target.checked;
    setOrderItems([...orderItems]);
  };

  const handleOrderItemDiagnosisChange = (value: string, index: number) => {
    orderItems[index].details.diagnosis = value;
    setOrderItems([...orderItems]);
  };

  const handleOrderItemDeliveredAmountChange = (value: number, index: number) => {
    orderItems[index].realAmount = value;
    setOrderItems([...orderItems]);
  };

  if (!order || !order.items) {
    return null;
  }

  return (
    <Dialog
      PaperProps={{ style: { minHeight: 'calc(100vh - 64px)' } }}
      id="confirmOrderDialog"
      open={opened}
      onClose={() => closeDialog(false)}
      maxWidth="lg"
      fullScreen={fullScreen}
      fullWidth
    >
      {fullScreen && (
        <AppBar>
          <Toolbar>
            <Box width="100%" justifyContent="space-between" display="flex">
              <Box display="flex" alignItems="center">
                <IconButton edge="start" color="inherit" onClick={() => closeDialog(false)} aria-label="close">
                  <CloseIcon />
                </IconButton>
                <Typography variant="h6">{t('f-supp-order-complete-title')}</Typography>
              </Box>

              <Tooltip title={t('f-supp-order-confirm')} arrow>
                <IconButton color="inherit" onClick={handleConfirmOrder} disabled={isBusy} id="saveBtn">
                  <Icon>{'done'}</Icon>
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <DialogTitle id="confirmOrderDialogTitle">{t('f-supp-order-confirm-title')}</DialogTitle>

      <DialogContent>
        <Grid container direction="column" spacing={1}>
          {renderOrderItemsSectionHeader()}

          {renderOrderItemsTable()}

          {renderDetailsSectionHeader()}

          {renderDetailsSectionContent()}
        </Grid>
      </DialogContent>

      <DialogActions>
        {isBusy && <CircularProgress />}

        {!fullScreen && (
          <>
            <Button
              onClick={() => closeDialog(false)}
              startIcon={<Icon>{'close'}</Icon>}
              disabled={isBusy}
              variant="outlined"
              color="primary"
            >
              {t('f-com-cancel')}
            </Button>

            <Button
              onClick={handleConfirmOrder}
              id="saveBtn"
              startIcon={<Icon>{'done_outline'}</Icon>}
              disabled={isBusy}
              variant="contained"
              color="primary"
            >
              {t('f-supp-order-confirm')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmSupplyOrderModal;
